import { useEffectOnce } from '@trmediaab/zebra-hooks';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Button from 'shared/components/button/Button';
import CloseButton from 'shared/components/button/CloseButton';
import Dropdown from 'shared/components/Dropdown';
import Checkmark from 'shared/components/icon/Checkmark';
import IconEdit from 'shared/components/icon/Edit';
import IconView from 'shared/components/icon/View';
import RadioInput from 'shared/components/input/RadioInput';
import SelectInput from 'shared/components/input/SelectInput';
import TextInput from 'shared/components/input/TextInput';
import Loader from 'shared/components/loader/Loader';
import Tabs from 'shared/components/tabs/Tabs';
import UrlSharer from 'shared/components/UrlSharer';
import { getText, isValidEmail } from 'shared/utils/Text';
import { toAbsoluteUrl } from 'shared/utils/urls';

import styles from './index.module.scss';

const AliasForm = ({ currentAlias, userEmail, onSubmitAlias, onCancel }) => {
  const [nameType, setNameType] = useState(
    !currentAlias || currentAlias === userEmail ? 'email' : 'custom',
  );
  const [customAlias, setCustomAlias] = useState(currentAlias ?? '');
  const usesCustomAlias = nameType === 'custom';
  const inputValue = usesCustomAlias ? customAlias : userEmail;
  return (
    <div className={styles.formWrapper}>
      <div className={styles.label}>
        {getText('dialog-collaborate-alias-label')}
      </div>
      <div>
        <div className={styles.radio}>
          <RadioInput
            name="nameType"
            value="email"
            checked={!usesCustomAlias}
            labelText={getText('dialog-collaborate-use-email-as-alias')}
            onChange={setNameType}
          />
        </div>
        <div className={styles.radio}>
          <RadioInput
            name="nameType"
            value="custom"
            checked={usesCustomAlias}
            labelText={getText('dialog-collaborate-use-custom-alias')}
            onChange={setNameType}
          />
        </div>
        <div className={styles.input}>
          <TextInput
            value={inputValue}
            onChange={setCustomAlias}
            disabled={!usesCustomAlias}
            maxLength={80}
            placeholder={getText('dialog-collaborate-custom-alias-placeholder')}
          />
        </div>
        <div className={styles.buttons}>
          <Button
            className={classnames(styles.btn, styles.submit)}
            onClick={() => onSubmitAlias(inputValue.trim())}
            disabled={
              inputValue.trim() === '' || inputValue.trim() === currentAlias
            }
          >
            {currentAlias
              ? getText('dialog-collaborate-alias-update-button')
              : getText('dialog-collaborate-alias-next-button')}
          </Button>
          {currentAlias && (
            <Button className={classnames(styles.btn)} onClick={onCancel}>
              {getText('dialog-collaborate-alias-cancel-button')}
            </Button>
          )}
        </div>
        <div className={styles.description}>
          {getText('dialog-collaborate-alias-explanation')}
        </div>
      </div>
    </div>
  );
};

const ShareDialog = ({
  previouslyInvitedEmails,
  userEmail,
  hasSubscription,
  collaborators: allCollaborators = [],
  resentInvites,
  loadingResentInvites,
  isOwner,
  collaboratorsError,
  loading,
  type,
  initialTab,

  closeHandler,
  createCollaborator,
  deleteCollaborator,
  fetchCollaborators,
  fetchSharedWithUsers,
  shareCode,
  shareCodeError,
  shareBet,
  updateCollaborator,
  resendCollaboratorInvite,
  clearResentInvites,
  setInviteAlias,
}) => {
  const me = allCollaborators.find(collaborator => collaborator.self);
  const myAlias = me?.alias;
  const [isEditingAlias, setIsEditingAlias] = useState(false);
  // Original collaborator array contains oneself, which should not
  // be included as part of sharing the game
  const collaborators = allCollaborators.filter(
    collaborator => !collaborator.owner,
  );

  const selectableContacts = previouslyInvitedEmails.map(email => ({
    text: email,
    value: email,
    disabled: collaborators.some(collaborator => collaborator.email === email),
  }));

  selectableContacts.unshift({
    text: getText('dialog-collaborate-previously-invited'),
    value: -1,
  });

  const menu = [
    {
      id: 'share',
      menuText: getText('dialog-share-title'),
    },
    {
      id: 'collaborate',
      menuText: getText('dialog-collaborate-title'),
    },
  ];

  const [isMenuExpanded, setMenuExpanded] = useState(false);
  const [activeItem, setActiveItem] = useState(initialTab ?? 'share');
  const [selectedContactId, setSelectedContactId] = useState(-1);

  const urlPrefix = type === 'sport' ? 's' : 't';
  const url = shareCode
    ? toAbsoluteUrl(`${urlPrefix}/${shareCode}`)
    : undefined;

  useEffectOnce(() => {
    shareBet();
    fetchCollaborators();
    fetchSharedWithUsers();
  });

  useEffect(
    () => () => {
      // Clears invites memory upon closing the dialog.
      // This gives the user a chance to resend invites next time the dialog is opened.
      clearResentInvites();
    },
    [clearResentInvites],
  );

  const actions = [
    {
      text: getText('dialog-collaborate-email-action-edit'),
      value: 'WRITE',
    },
    {
      text: getText('dialog-collaborate-email-action-view'),
      value: 'READ',
    },
    {
      text: getText('dialog-collaborate-email-action-delete'),
      value: 'DELETE',
    },
  ];

  const [collaborateType, setCollaborateType] = useState(actions[0].value);
  const [email, setEmail] = useState('');

  const handleCreateCollaborator = () => {
    createCollaborator({ email, collaborateType });
    setEmail('');
    setSelectedContactId(-1);
  };

  const handleContactSelection = selectedEmail => {
    setSelectedContactId(selectedEmail);
    if (previouslyInvitedEmails.includes(selectedEmail)) {
      setEmail(selectedEmail);
    }
  };

  const handleEmailInputChange = email => {
    setEmail(email);
    const selectedContact = previouslyInvitedEmails.find(
      _email => _email === email,
    );
    setSelectedContactId(selectedContact ? selectedContact.id : -1);
  };

  const handleSubmitAlias = async alias => {
    await setInviteAlias(alias);
    fetchCollaborators();
    setIsEditingAlias(false);
  };

  const changeCollaborator = (user, collaborateType) => {
    if (user.type === collaborateType) {
      return;
    }

    if (collaborateType === 'DELETE') {
      deleteCollaborator(user.id);
    } else {
      updateCollaborator({
        userId: user.id,
        collaborateType,
      });
    }
  };

  // Prevents sending invites to invalid or existing email addresses.
  // Since email addresses are case insensitive, we compare lowercase.
  const normalizedInputEmail = email.trim().toLowerCase();
  const emailAlreadyExists = allCollaborators.some(
    collaborator => collaborator.email?.toLowerCase() === normalizedInputEmail,
  );
  const isSubmitBtnDisabled = !isValidEmail(email) || emailAlreadyExists;

  // On the Norwegian site, non-subscription users can create a new bet as
  // an owner, but they are still not entitled to make it a collaborative bet
  const showCollaborationTab = isOwner && !(IS_NO_SITE && !hasSubscription);

  return (
    <>
      <Loader backgroundAlpha="light" cover visible={loading} />
      {showCollaborationTab && (
        <Tabs
          clickHandler={item => setActiveItem(item)}
          closeHandler={closeHandler}
          items={menu}
          selectedId={activeItem}
          showExpanded={isMenuExpanded}
          toggleHandler={() => setMenuExpanded(!isMenuExpanded)}
        />
      )}

      <div className={styles.body}>
        {!showCollaborationTab && (
          <>
            <div className={styles.title}>{getText('dialog-share-title')}</div>
            <CloseButton
              className={styles.titleCloseButton}
              onClick={closeHandler}
            />
          </>
        )}
        {activeItem === 'share' && (
          <>
            <p>{getText('dialog-share-body')}</p>
            {!shareCodeError ? (
              <UrlSharer
                mailBody={getText('email-body-share', url)}
                url={url}
              />
            ) : (
              <div className={styles.error}>
                <b>{getText('error-message-generic')}</b>
              </div>
            )}
          </>
        )}

        {activeItem === 'collaborate' && (
          <>
            {myAlias && !isEditingAlias && (
              <div className={styles.header}>
                {getText('dialog-collaborate-my-alias-label')}
                <Button
                  className={styles.aliasButton}
                  onClick={() => setIsEditingAlias(true)}
                >
                  <span>{myAlias}</span> <IconEdit />
                </Button>
              </div>
            )}
            {!myAlias || isEditingAlias ? (
              <AliasForm
                currentAlias={myAlias}
                userEmail={userEmail}
                onSubmitAlias={handleSubmitAlias}
                onCancel={() => setIsEditingAlias(false)}
              />
            ) : (
              <>
                {collaborators.length > 0 && (
                  <div className={styles.collaborators}>
                    <div className={styles.header}>
                      {getText('dialog-collaborate-list-header')}
                    </div>

                    <ul>
                      {collaborators.map(user => (
                        <li className={styles.item} key={user.id}>
                          <div className={styles.email}>
                            <div>{user.email}</div>
                            {user.alias && <div>Alias: {user.alias}</div>}
                            {user.accepted ? (
                              <div className={styles.acceptedInvite}>
                                <Checkmark />
                                <span>
                                  {getText(
                                    'dialog-collaborate-invite-accepted',
                                  )}
                                </span>
                              </div>
                            ) : (
                              <div className={styles.pendingInvite}>
                                <Checkmark />
                                <span>
                                  {getText('dialog-collaborate-invite-sent')}
                                </span>
                                <Button
                                  className={styles.resendButton}
                                  disabled={
                                    loadingResentInvites.includes(user.id) ||
                                    resentInvites.includes(user.id)
                                  }
                                  onClick={() =>
                                    resendCollaboratorInvite(user.id)
                                  }
                                >
                                  {resentInvites.includes(user.id)
                                    ? getText(
                                        'dialog-collaborate-invite-resent',
                                      )
                                    : getText(
                                        'dialog-collaborate-resend-invite',
                                      )}
                                </Button>
                              </div>
                            )}
                          </div>
                          <Dropdown
                            items={actions}
                            onChange={({ value }) =>
                              changeCollaborator(user, value)
                            }
                            selected={user.type}
                            transparent
                          >
                            {user.type === 'WRITE' ? (
                              <IconEdit className={styles.icon} />
                            ) : (
                              <IconView className={styles.icon} />
                            )}
                          </Dropdown>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className={styles.formWrapper}>
                  <div className={styles.label}>
                    {getText('dialog-collaborate-email-label')}
                  </div>
                  {previouslyInvitedEmails.length > 0 && (
                    <div className={styles.contactsInput}>
                      <SelectInput
                        width="220"
                        value={selectedContactId}
                        onChange={e => handleContactSelection(e.target.value)}
                      >
                        {selectableContacts.map(contact => (
                          <option
                            key={contact.value}
                            value={contact.value}
                            disabled={contact.disabled}
                          >
                            {contact.text}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                  )}

                  <div className={styles.input}>
                    <TextInput
                      onChange={handleEmailInputChange}
                      placeholder={getText(
                        'dialog-collaborate-email-placeholder',
                      )}
                      type="email"
                      value={email}
                    />
                    <Dropdown
                      items={actions.filter(a => a.value !== 'DELETE')}
                      onChange={({ value }) => setCollaborateType(value)}
                      selected={collaborateType}
                    >
                      {collaborateType === 'WRITE' ? (
                        <IconEdit className={styles.icon} />
                      ) : (
                        <IconView className={styles.icon} />
                      )}
                    </Dropdown>
                  </div>
                  <div className={styles.buttons}>
                    <Button
                      className={classnames(styles.btn, styles.submit)}
                      disabled={isSubmitBtnDisabled}
                      onClick={handleCreateCollaborator}
                    >
                      {getText('dialog-collaborate-btn-submit')}
                    </Button>
                  </div>
                  <div className={styles.description}>
                    {getText(`dialog-collaborate-invite-explanation_${type}`)}
                  </div>
                </div>
              </>
            )}
            {collaboratorsError && (
              <div className={styles.error}>
                <b>{getText('error-message-generic')}</b>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

ShareDialog.propTypes = {
  collaborators: PropTypes.arrayOf(PropTypes.any),
  collaboratorsError: PropTypes.any,
  loading: PropTypes.bool.isRequired,
  shareCode: PropTypes.string,
  shareCodeError: PropTypes.string,
  type: PropTypes.oneOf(['sport', 'atg']),

  closeHandler: PropTypes.func.isRequired,
  createCollaborator: PropTypes.func.isRequired,
  deleteCollaborator: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  shareBet: PropTypes.func.isRequired,
  updateCollaborator: PropTypes.func.isRequired,
};

export default ShareDialog;
